// @flow
import styled from '@emotion/styled';
import { SPACING, FONT_WEIGHTS, typography } from '@tablekit/theme';
import LazyLoad from 'react-lazyload';
import { getPageContext } from 'components/PageContext';
import type {
  BlokComponentPropsType,
  CompanyProfileType,
  TitleWithDescriptionType
} from 'components/types';
import RichText from 'components/RichText';
import { LAPTOP_BREAKPOINT, MOBILE_BREAKPOINT } from 'utils/theme';
import columnWidth from 'utils/columnWidth';
import renderComponent from 'components/editable/renderComponent';
import { imageTransform } from 'components/Image';
import * as componentKeys from '../../componentKeys';

const LOGO_HEIGHT = 50;

const singleColumnIndent = columnWidth(1);

const Container = styled.div`
  ${typography('L4')};
`;

const LogoWrapper = styled.div`
  @media (min-width: 576px) {
    margin: 0 ${singleColumnIndent}%;
  }
`;

const LogoImage = styled.img`
  height: ${LOGO_HEIGHT}px;

  @media ${MOBILE_BREAKPOINT} {
    height: 35px;
  }
`;

const OfficeBlock = styled.div`
  margin-top: ${SPACING.L4};
`;

const OfficeTitle = styled.div`
  font-weight: ${FONT_WEIGHTS.BOLD};
  margin-bottom: ${SPACING.L2};
`;

const ProfileWrapper = styled.div`
  @media (min-width: 576px) {
    margin: 0 ${singleColumnIndent}%;
    width: ${columnWidth(10)}%;
  }

  @media (min-width: 1280px) {
    margin: 0 ${singleColumnIndent}%;
    width: ${columnWidth(6)}%;
  }
`;
const OfficesWrapper = styled.div`
  @media (min-width: 576px) {
    margin: 0 ${singleColumnIndent}%;
    width: ${columnWidth(10)}%;
  }

  @media (min-width: 1280px) {
    margin: 0 ${singleColumnIndent}%;
    width: ${columnWidth(7)}%;
  }
`;

const ProfileTitle = styled.h5`
  display: block;
  font-weight: ${FONT_WEIGHTS.BOLD};
  ${typography('L7')};
  margin-top: ${SPACING.L7};
  margin-bottom: ${SPACING.L6};
`;

const ProfileColumn = styled.div`
  display: flex;
  flex-direction: column;

  @media ${LAPTOP_BREAKPOINT} {
    display: table;
    border-spacing: ${SPACING.L4};
    margin: -${SPACING.L4};
    flex: 1 1 50%;
  }
`;

const ProfileRow = styled.div`
  margin-bottom: ${SPACING.L4};

  @media ${LAPTOP_BREAKPOINT} {
    display: table-row;
    & > * {
      display: table-cell;
    }
  }
`;

const ProfileRowTitle = styled.div`
  font-weight: ${FONT_WEIGHTS.BOLD};
  max-width: 100%;
  margin-bottom: ${SPACING.L2};

  @media ${LAPTOP_BREAKPOINT} {
    margin-right: ${SPACING.L5};
  }
`;

const ProfileRowDescription = styled(RichText)`
  padding-right: ${SPACING.L4};
  max-width: 100%;
`;

const officesComponentMap = {
  [componentKeys.TITLE_WITH_DESCRIPTION]: ({
    blok: { title, description },
    ...others
  }: BlokComponentPropsType<TitleWithDescriptionType>) => (
    <OfficeBlock {...others}>
      <OfficeTitle>{title}</OfficeTitle>
      <RichText richText={description} />
    </OfficeBlock>
  )
};

const profileColumnComponentMap = {
  [componentKeys.TITLE_WITH_DESCRIPTION]: ({
    blok: { title, description },
    ...others
  }: BlokComponentPropsType<TitleWithDescriptionType>) => (
    <ProfileRow {...others}>
      <ProfileRowTitle>{title}</ProfileRowTitle>
      <ProfileRowDescription richText={description} />
    </ProfileRow>
  )
};

const CompanyProfile = ({
  blok: {
    offices = [],
    offices_title: officesTitle,
    profile_column_left: leftColumnContent = [],
    profile_column_right: rightColumnContent = [],
    profile_title: profileTitle
  }
}: BlokComponentPropsType<CompanyProfileType>) => {
  const {
    layout: {
      logo: [logo]
    }
  } = getPageContext();
  return (
    <Container>
      <LogoWrapper>
        <LazyLoad offset={LOGO_HEIGHT}>
          <LogoImage src={imageTransform(logo.colored)} alt={logo.alt} />
        </LazyLoad>
      </LogoWrapper>
      <ProfileWrapper>
        {profileTitle && <ProfileTitle>{profileTitle}</ProfileTitle>}
        <ProfileColumn>
          {leftColumnContent.concat(rightColumnContent || []).map((blok) =>
            renderComponent(
              blok,
              {
                key: blok._uid,
                customPath:
                  'components/editable/CompanyProfile.jsx_ProfileColumn'
              },
              profileColumnComponentMap
            )
          )}
        </ProfileColumn>
      </ProfileWrapper>
      <OfficesWrapper>
        {officesTitle && <ProfileTitle>{officesTitle}</ProfileTitle>}
        {offices.map((blok) =>
          renderComponent(
            blok,
            {
              key: blok._uid,
              customPath: 'components/editable/CompanyProfile.jsx_Offices'
            },
            officesComponentMap
          )
        )}
      </OfficesWrapper>
    </Container>
  );
};

export default CompanyProfile;
